import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";

// EPC Label enum matching backend
export enum EPCLabel {
  A_PLUS_PLUS = "A++",
  A_PLUS = "A+",
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
  F = "F",
  G = "G",
  UNKNOWN = "Onbekend",
  NIET_VAN_TOEPASSING = "Niet van toepassing",
  GEMEENSCHAPPELIJKE_DELEN = "Gemeenschappelijke delen",
  ONTBREKEND = "Ontbrekend",
}

// Project part types mirroring the backend DTO
export type ProjectPartLabelType =
  | "esthetisch"
  | "algemeen"
  | "energetisch"
  | "veiligheid"
  | null;

export interface ProjectPart {
  id: number;
  title: string;
  labelName: string | null;
  labelType: ProjectPartLabelType;
  plannedStartDate: string | null;
  actualStartDate: string | null;
  effectiveStartDate: string | null;
  /** Investment for this specific part (BE contract §2) */
  investedPlanned: number;
  investedActual: number;
}

export interface ProjectPartGroup {
  projectId: number;
  projectTitle: string;
  parts: ProjectPart[];
}

/** Per-category investment shape (BE contract §1). All fields default 0, never null. */
export interface InvestmentByCategory {
  energetisch: { planned: number; actual: number };
  esthetisch: { planned: number; actual: number };
  veiligheid: { planned: number; actual: number };
  algemeen: { planned: number; actual: number };
}

/** Unit-level investment totals (BE contract §1). */
export interface UnitInvestment {
  plannedTotal: number;
  actualTotal: number;
  byCategory: InvestmentByCategory;
}

export interface EPCData {
  unitId: string;
  unitLocation: string;
  unitType: string;
  buildingId: string;
  buildingLocation: string;
  location: string;
  type: string;
  epcEvolution: "improved" | "declined" | "unchanged" | null;
  currentEPC: {
    label: EPCLabel;
    kWh: string;
    documentDate: string;
    fileLogId?: number;
    sharePointFileUrl?: string;
  } | null;
  epcHistory: Array<{
    label: EPCLabel;
    kWh: string;
    documentDate: string;
    fileLogId: number;
    sharePointFileUrl?: string;
  }>;
  // Fields added for renovation integration (plan §1.5)
  projectPartGroups: ProjectPartGroup[];
  epcRefreshSuggested: boolean;
  epcRefreshReason: "ENERGETIC" | "GENERIC" | null;
  latestProjectPartDate: string | null;
  latestEpcDocumentDate: string | null;
  /** Investment data per unit — BE contract §1. Defaults to zero object when not present. */
  investment: UnitInvestment;
}

export interface EPCMetrics {
  totalUnitsWithEPC: number;
  totalUnits: number;
  totalBuildings: number;
  buildingsWithEPC: number;
  gemeenschappelijkeDelen: number;
  averageEPCImprovement: number;
  epcDistribution: Record<EPCLabel, number>;
  improvementTrends: {
    improved: number;
    declined: number;
    unchanged: number;
  };
  /** Top-level investment metrics (BE contract §1). Default 0 when not present. */
  totalInvestedPlanned: number;
  totalInvestedActual: number;
  investedByCategory: InvestmentByCategory;
  energeticShareOfBudget: number; // 0–1
  unitsImproved: number;
  unitsNeedingNewEpc: number;
}

export interface EPCSummaryIssues {
  unitsWithEPC: number;
  totalUnits: number;
  buildingsWithEPC: number;
  totalBuildings: number;
  improved: number;
  declined: number;
  unchanged: number;
  missingEPC: number;
}

// EPC Label score for calculations
export const getEPCScore = (label: EPCLabel): number => {
  switch (label) {
    case EPCLabel.A_PLUS_PLUS:
      return 8;
    case EPCLabel.A_PLUS:
      return 7;
    case EPCLabel.A:
      return 6;
    case EPCLabel.B:
      return 5;
    case EPCLabel.C:
      return 4;
    case EPCLabel.D:
      return 3;
    case EPCLabel.E:
      return 2;
    case EPCLabel.F:
      return 1;
    case EPCLabel.G:
      return 0;
    case EPCLabel.NIET_VAN_TOEPASSING:
      return -1;
    case EPCLabel.GEMEENSCHAPPELIJKE_DELEN:
      return -1;
    case EPCLabel.ONTBREKEND:
      return -1;
    default:
      return -1;
  }
};

// EPC Label color mapping
export const getEPCColor = (label: EPCLabel): string => {
  switch (label) {
    case EPCLabel.A_PLUS_PLUS:
    case EPCLabel.A_PLUS:
    case EPCLabel.A:
      return "bg-status-good text-white";
    case EPCLabel.B:
      return "bg-moss-primary text-white";
    case EPCLabel.C:
      return "bg-yellow-500 text-white";
    case EPCLabel.D:
      return "bg-orange-500 text-white";
    case EPCLabel.E:
    case EPCLabel.F:
    case EPCLabel.G:
      return "bg-brick-primary text-white";
    case EPCLabel.GEMEENSCHAPPELIJKE_DELEN:
      return "bg-neutral-primary text-white";
    default:
      return "bg-neutral-secondary-mid text-white";
  }
};

// Helper function to display EPC label
export const getDisplayLabel = (label: EPCLabel): string => {
  if (label === EPCLabel.UNKNOWN || label === EPCLabel.ONTBREKEND) {
    return "O";
  }
  if (label === EPCLabel.NIET_VAN_TOEPASSING) {
    return "NVT";
  }
  return label;
};

// Safe date parsing for Safari compatibility
const safeParseDate = (dateStr: string | null | undefined): number => {
  if (!dateStr) return 0;
  try {
    const timestamp = new Date(dateStr).getTime();
    return isNaN(timestamp) ? 0 : timestamp;
  } catch {
    return 0;
  }
};

// Calculate improvement trend
const getImprovementTrend = (
  history: EPCData["epcHistory"],
): "improved" | "declined" | "unchanged" | null => {
  if (!history || history.length < 2) {
    return null;
  }

  const sortedHistory = [...history].sort((a, b) => {
    const dateA = safeParseDate(a.documentDate);
    const dateB = safeParseDate(b.documentDate);
    if (dateA === 0 && dateB === 0) return 0;
    if (dateA === 0) return 1;
    if (dateB === 0) return -1;
    return dateA - dateB;
  });

  const oldest = sortedHistory[0];
  const newest = sortedHistory[sortedHistory.length - 1];

  const oldestScore = getEPCScore(oldest.label);
  const newestScore = getEPCScore(newest.label);

  if (newestScore > oldestScore) {
    return "improved";
  } else if (newestScore < oldestScore) {
    return "declined";
  } else {
    return "unchanged";
  }
};

export function useEnergyPerformanceData() {
  const { session } = useSession();
  const [epcData, setEpcData] = useState<EPCData[]>([]);
  const [metrics, setMetrics] = useState<EPCMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use refs to prevent re-fetching when switching tabs
  const hasInitiallyLoaded = useRef(false);
  const isCurrentlyFetching = useRef(false);

  const fetchData = useCallback(
    async (force = false) => {
      if (!force && hasInitiallyLoaded.current && epcData.length > 0) {
        return;
      }

      if (isCurrentlyFetching.current) {
        return;
      }

      if (!session) {
        return;
      }

      isCurrentlyFetching.current = true;

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchWithAuth(
          `${API_URL}/maintenance/energy-performance`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const responseData = await response.json();
        const { units = [], metrics: rawMetrics = {} } = responseData;

        // Provide zero-defaults for new investment metric fields (BE may not yet return them)
        const defaultCategoryInvestment: InvestmentByCategory = {
          energetisch: { planned: 0, actual: 0 },
          esthetisch: { planned: 0, actual: 0 },
          veiligheid: { planned: 0, actual: 0 },
          algemeen: { planned: 0, actual: 0 },
        };
        const metricsData: EPCMetrics = {
          ...rawMetrics,
          totalInvestedPlanned: rawMetrics.totalInvestedPlanned ?? 0,
          totalInvestedActual: rawMetrics.totalInvestedActual ?? 0,
          investedByCategory:
            rawMetrics.investedByCategory ?? defaultCategoryInvestment,
          energeticShareOfBudget: rawMetrics.energeticShareOfBudget ?? 0,
          unitsImproved: rawMetrics.unitsImproved ?? 0,
          unitsNeedingNewEpc: rawMetrics.unitsNeedingNewEpc ?? 0,
        };

        // Zero-object defaults for investment fields — BE may not yet return these
        const defaultInvestment: UnitInvestment = {
          plannedTotal: 0,
          actualTotal: 0,
          byCategory: {
            energetisch: { planned: 0, actual: 0 },
            esthetisch: { planned: 0, actual: 0 },
            veiligheid: { planned: 0, actual: 0 },
            algemeen: { planned: 0, actual: 0 },
          },
        };

        // Process data to add computed properties
        const processedData: EPCData[] = units.map((unit: any) => ({
          ...unit,
          location: unit.unitLocation || unit.buildingLocation,
          type: unit.unitType,
          epcEvolution: getImprovementTrend(unit.epcHistory),
          investment: unit.investment ?? defaultInvestment,
          // Ensure each ProjectPart has investment defaults too
          projectPartGroups: (unit.projectPartGroups ?? []).map((g: any) => ({
            ...g,
            parts: (g.parts ?? []).map((p: any) => ({
              ...p,
              investedPlanned: p.investedPlanned ?? 0,
              investedActual: p.investedActual ?? 0,
            })),
          })),
        }));

        setEpcData(processedData);
        setMetrics(metricsData);
        hasInitiallyLoaded.current = true;
      } catch (err) {
        console.error("Error fetching energy performance data:", err);
        setError("Kon energieprestatie gegevens niet laden");
        toast.error("Kon energieprestatie gegevens niet laden");
      } finally {
        setIsLoading(false);
        isCurrentlyFetching.current = false;
      }
    },
    [session, epcData.length],
  );

  // Refresh data (force fetch)
  const refresh = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  // Initial fetch on mount
  useEffect(() => {
    if (session && !hasInitiallyLoaded.current) {
      fetchData();
    }
  }, [session, fetchData]);

  // Calculate summary issues
  const summaryIssues = useMemo<EPCSummaryIssues>(() => {
    if (!metrics) {
      return {
        unitsWithEPC: 0,
        totalUnits: 0,
        buildingsWithEPC: 0,
        totalBuildings: 0,
        improved: 0,
        declined: 0,
        unchanged: 0,
        missingEPC: 0,
      };
    }

    return {
      unitsWithEPC: metrics.totalUnitsWithEPC,
      totalUnits: metrics.totalUnits,
      buildingsWithEPC: metrics.buildingsWithEPC,
      totalBuildings: metrics.totalBuildings,
      improved: metrics.improvementTrends.improved,
      declined: metrics.improvementTrends.declined,
      unchanged: metrics.improvementTrends.unchanged,
      missingEPC: metrics.totalUnits - metrics.totalUnitsWithEPC,
    };
  }, [metrics]);

  // Update single EPC entry (optimistic update)
  const refreshSingleEPC = useCallback(
    async (fileLogId: number) => {
      if (!session) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/file-log-with-epc/${fileLogId}`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const fileLogWithEpc = await response.json();
          const updatedEPC = fileLogWithEpc.epc;

          setEpcData((prevData) =>
            prevData.map((unit) => {
              const hasCurrentEPC = unit.currentEPC?.fileLogId === fileLogId;
              const hasHistoryEPC = unit.epcHistory.some(
                (epc) => epc.fileLogId === fileLogId,
              );

              if (hasCurrentEPC || hasHistoryEPC) {
                let updatedUnit = { ...unit };

                if (hasCurrentEPC && updatedEPC) {
                  updatedUnit.currentEPC = {
                    ...unit.currentEPC!,
                    label: updatedEPC.epcLabel,
                    kWh: updatedEPC.kWh?.toString() || "",
                    documentDate:
                      updatedEPC.documentDate || unit.currentEPC!.documentDate,
                  };
                }

                if (hasHistoryEPC && updatedEPC) {
                  updatedUnit.epcHistory = unit.epcHistory.map((epc) =>
                    epc.fileLogId === fileLogId
                      ? {
                          ...epc,
                          label: updatedEPC.epcLabel,
                          kWh: updatedEPC.kWh?.toString() || "",
                          documentDate:
                            updatedEPC.documentDate || epc.documentDate,
                        }
                      : epc,
                  );
                  updatedUnit.epcEvolution = getImprovementTrend(
                    updatedUnit.epcHistory,
                  );
                }

                return updatedUnit;
              }
              return unit;
            }),
          );
        }
      } catch (err) {
        console.error("Error refreshing single EPC:", err);
        refresh();
      }
    },
    [session, refresh],
  );

  // Delete EPC entry
  const deleteEPC = useCallback(
    async (fileLogId: number): Promise<boolean> => {
      if (!session) return false;

      try {
        let fileToDelete;

        try {
          const fileResponse = await fetchWithAuth(
            `${API_URL}/file/file-log-with-epc/${fileLogId}`,
            { method: "GET" },
            session,
          );

          if (fileResponse.ok) {
            const fileData = await fileResponse.json();
            fileToDelete = {
              id: fileLogId,
              sharePointFileId:
                fileData.sharePointFileId ||
                (fileData.sharePointFileUrl
                  ? fileData.sharePointFileUrl.split("/").pop()
                  : null),
              sharePointFileUrl: fileData.sharePointFileUrl,
            };
          } else {
            fileToDelete = {
              id: fileLogId,
              sharePointFileId: null,
              sharePointFileUrl: null,
            };
          }
        } catch {
          fileToDelete = {
            id: fileLogId,
            sharePointFileId: null,
            sharePointFileUrl: null,
          };
        }

        const response = await fetchWithAuth(
          `${API_URL}/file`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fileToDelete),
          },
          session,
        );

        if (response.status === 200) {
          // Optimistic update
          setEpcData((prevData) =>
            prevData.map((unit) => {
              const hasCurrentEPC = unit.currentEPC?.fileLogId === fileLogId;
              const hasHistoryEPC = unit.epcHistory.some(
                (epc) => epc.fileLogId === fileLogId,
              );

              if (hasCurrentEPC || hasHistoryEPC) {
                const updatedCurrentEPC = hasCurrentEPC
                  ? null
                  : unit.currentEPC;
                const updatedHistory = unit.epcHistory.filter(
                  (epc) => epc.fileLogId !== fileLogId,
                );
                const updatedEvolution = getImprovementTrend(updatedHistory);

                return {
                  ...unit,
                  currentEPC: updatedCurrentEPC,
                  epcHistory: updatedHistory,
                  epcEvolution: updatedEvolution,
                };
              }
              return unit;
            }),
          );

          toast.success("EPC succesvol verwijderd!");
          return true;
        } else {
          toast.error("Fout bij het verwijderen van EPC");
          return false;
        }
      } catch (err) {
        console.error("Error deleting EPC:", err);
        toast.error("Fout bij het verwijderen van EPC");
        return false;
      }
    },
    [session],
  );

  // Portfolio-level average EPC score (0–8 scale, only rows with a real EPC)
  const avgEPCScore = useMemo(() => {
    const scoreable = epcData.filter(
      (u) =>
        u.currentEPC &&
        u.currentEPC.label !== EPCLabel.ONTBREKEND &&
        u.currentEPC.label !== EPCLabel.UNKNOWN &&
        u.currentEPC.label !== EPCLabel.NIET_VAN_TOEPASSING &&
        u.currentEPC.label !== EPCLabel.GEMEENSCHAPPELIJKE_DELEN,
    );
    if (scoreable.length === 0) return null;
    const total = scoreable.reduce(
      (sum, u) => sum + getEPCScore(u.currentEPC!.label),
      0,
    );
    return total / scoreable.length;
  }, [epcData]);

  // Map numeric average score back to an approximate label
  const avgEPCLabel = useMemo((): EPCLabel | null => {
    if (avgEPCScore === null) return null;
    if (avgEPCScore >= 7.5) return EPCLabel.A_PLUS_PLUS;
    if (avgEPCScore >= 6.5) return EPCLabel.A_PLUS;
    if (avgEPCScore >= 5.5) return EPCLabel.A;
    if (avgEPCScore >= 4.5) return EPCLabel.B;
    if (avgEPCScore >= 3.5) return EPCLabel.C;
    if (avgEPCScore >= 2.5) return EPCLabel.D;
    if (avgEPCScore >= 1.5) return EPCLabel.E;
    if (avgEPCScore >= 0.5) return EPCLabel.F;
    return EPCLabel.G;
  }, [avgEPCScore]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const epcLabels = Array.from(
      new Set(
        epcData
          .map((unit) => unit.currentEPC?.label)
          .filter(Boolean) as string[],
      ),
    );

    const unitTypes = Array.from(new Set(epcData.map((unit) => unit.unitType)));

    return { epcLabels, unitTypes };
  }, [epcData]);

  return {
    epcData,
    metrics,
    isLoading,
    error,
    summaryIssues,
    filterOptions,
    refresh,
    refreshSingleEPC,
    deleteEPC,
    hasLoaded: hasInitiallyLoaded.current,
    getImprovementTrend,
    avgEPCScore,
    avgEPCLabel,
  };
}
